import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  DeletePerformanceReviewDocument,
  PerformanceReviewProcessesDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import TextField from 'modules/core/components/TextField'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { Review } from '../types'

export const DeleteReviewModal: React.FC<{ review: Review; onClose: () => void }> = ({
  review,
  onClose,
}) => {
  const [inputName, setInputName] = React.useState<string>('')
  const name = review.name
  const inputIsOk = inputName.trim() === name?.trim()
  const [deleteReview] = useMutation(DeletePerformanceReviewDocument, {
    extractorFn: (data) => data.performanceMutations.deletePerformanceReviewProcess,
    update(cache) {
      cache.evict({
        id: cache.identify({ id: review.id, __typename: review.__typename }),
      })
    },
  })

  const { running, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      const response = await deleteReview({
        awaitRefetchQueries: true,
        variables: {
          id: review.id,
        },
        refetchQueries: [
          {
            query: PerformanceReviewProcessesDocument,
            variables: {
              ids: [],
            },
          },
        ],
      })

      const handled = handleResult(response, () => undefined)

      closePortal()
      return handled
    },
    {
      successMessage: i18n.t('common.removed_successfully'),
      successFlash: true,
      errorFlash: true,
    }
  )

  return (
    <Modal
      title={i18n.t('performance_process.reviews.delete_review.modal.title')}
      description={i18n.t('performance_process.reviews.delete_review.modal.description')}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={running || !inputIsOk} submit>
            {i18n.t('common.delete')}
          </Button>
        </>
      }
      illustration="alert"
      onClose={onClose}
      onSubmit={onSubmit}
    >
      <Box flexDirection="column" gap="s8">
        <Text.Normal whiteSpace="breakSpaces">
          {i18n.t('performance_process.reviews.delete_review.modal.deletion_input', { name })}
        </Text.Normal>
        <TextField onChange={setInputName} value={inputName} />
      </Box>
    </Modal>
  )
}
